import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    X, Package, Users, UserPlus, FileText, Receipt, Wallet2, Image,
    LayoutDashboard, Tag, Type, Menu, Share2, PiggyBank, Shield,
    Layers, CheckSquare, Briefcase, Users2,
    ReceiptIndianRupeeIcon
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BottomButton = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        // cleanup in case component unmounts while modal open
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [open]);

    const options = [
        { name: "Product", icon: Package },
        { name: "Lead", icon: Users },
        { name: "Customer", icon: UserPlus },
        { name: "Invoice", icon: FileText },
        { name: "Receipt", icon: ReceiptIndianRupeeIcon },
        { name: "Expense", icon: Wallet2 },
        { name: "Slider", icon: Image },
        { name: "Home Section", icon: LayoutDashboard },
        { name: "Offer", icon: Tag },
        { name: "Headline", icon: Type },
        { name: "Menu", icon: Menu },
        { name: "Referral", icon: Share2 },
        { name: "Wallet", icon: PiggyBank },
        { name: "Brand", icon: Shield },
        { name: "Plan", icon: Layers },
        { name: "Task", icon: CheckSquare },
        { name: "Job", icon: Briefcase },
        { name: "Team Member", icon: Users2 },
    ];

    const handleItemClick = (item) => {
        console.log(`${item.name} clicked`);

        if (item.name === "Product") navigate("/product-ai");
        if (item.name === "Lead") {
            const params = new URLSearchParams({ openEnquiry: "true", voice: "true" });
            navigate(`/crm?${params.toString()}`, { replace: true });
        }
        if (item.name === "Customer") navigate("/add-customer");
        if (item.name === "Invoice") navigate("/new-invoice");
        if (item.name === "Receipt") {
            const params = new URLSearchParams({ openReceipt: "true" });
            navigate(`/receipt?${params.toString()}`, { replace: true });
        }
        if (item.name === "Expense") {
            const params = new URLSearchParams({ openExpense: "true" });
            navigate(`/expense?${params.toString()}`, { replace: true });
        }
        if (item.name === "Slider") {
            const params = new URLSearchParams({ openSlider: "true" });
            navigate(`/slider?${params.toString()}`, { replace: true });
        }
        if (item.name === "Home Section") {
            const params = new URLSearchParams({ openSection: "true" });
            navigate(`/newarrival?${params.toString()}`, { replace: true });
        }
        if (item.name === "Offer") {
            const params = new URLSearchParams({ openOffer: "true" });
            navigate(`/offer?${params.toString()}`, { replace: true });
        }
        if (item.name === "Headline") {
            const params = new URLSearchParams({ openHeadline: "true" });
            navigate(`/headline?${params.toString()}`, { replace: true });
        }
        if (item.name === "Menu") navigate("/menus");
        if (item.name === "Referral") {
            const params = new URLSearchParams({ openRefer: "true" });
            navigate(`/refer?${params.toString()}`, { replace: true });
        }
        if (item.name === "Wallet") {
            const params = new URLSearchParams({ openWallet: "true" });
            navigate(`/wallet-target?${params.toString()}`, { replace: true });
        }
        if (item.name === "Brand") {
            const params = new URLSearchParams({ openBrand: "true" });
            navigate(`/brand?${params.toString()}`, { replace: true });
        }
        if (item.name === "Plan") {
            const params = new URLSearchParams({ openPlan: "true" });
            navigate(`/plan?${params.toString()}`, { replace: true });
        }
        if (item.name === "Task") {
            const params = new URLSearchParams({ openTask: "true" });
            navigate(`/tasks?${params.toString()}`, { replace: true });
        }
        if (item.name === "Job") {
            const params = new URLSearchParams({ openJob: "true" });
            navigate(`/jobs?${params.toString()}`, { replace: true });
        }
        if (item.name === "Team Member") navigate("/new-staff");

        setOpen(false);
    };

    return (
        <>
            <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
                <button
                    onClick={() => setOpen(true)}
                    className="cssbuttons-io-button">
                    <svg
                        height="24"
                        width="24"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" fill="currentColor"></path>
                    </svg>
                    <span>Add/Create</span>
                </button>
            </div>

            {/* Modal — portaled to body so it centers on the full viewport */}
            {createPortal(
                <AnimatePresence>
                    {open && (
                        <>
                            <motion.div
                                className="fixed inset-0 z-[9998] bg-black/50"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setOpen(false)}
                            />

                            <div
                                className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
                                onClick={() => setOpen(false)}
                                role="presentation"
                            >
                                <motion.div
                                    className="w-full max-w-[500px] max-h-[min(70vh,600px)] overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ duration: 0.3, ease: "easeOut" }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="mb-4 pb-2 border-b border-gray-300 flex justify-between items-center gap-2">
                                        <h3 className="text-lg font-semibold text-gray-800">
                                            Quick Actions
                                        </h3>
                                        <span
                                            onClick={() => setOpen(false)}
                                            className="cursor-pointer size-8 rounded-full flex justify-center items-center bg-gray-50 text-black hover:bg-gray-200 transition-colors"
                                        >
                                            <X size={18} />
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {options.map(({ name, icon: Icon }, idx) => (
                                            <motion.div
                                                key={idx}
                                                onClick={() => handleItemClick({ name })}
                                                className="cursor-pointer flex items-center justify-center gap-2 text-center text-sm font-medium text-gray-800 rounded-xl px-1 py-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-all duration-200"
                                            >
                                                <Icon className="mb-1 text-blue-600 shrink-0" size={18} />
                                                {name}
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>
                            </div>
                        </>
                    )}
                </AnimatePresence>,
                document.body
            )}

        </>
    );
};

export default BottomButton;
